/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiProgress,
  EuiSpacer,
  EuiText,
} from "@inscopix/ideas-eui";
import { CallOutError } from "components/CallOutError/CallOutError";
import { DropZoneBasic } from "components/DropZoneBasic/DropZoneBasic";
import { useEffect, useState } from "react";
import { ToolEditorFormCodeFileState } from "./ToolEditorFormCodeFileState";
import { useToolEditorContext } from "components/ToolEditor/Provider/ToolEditorProvider";
import {
  InvalidBundleError,
  validateBundle,
} from "../../helpers/validateBundle";

export type ToolEditorBundleFileState =
  | { state: "LOADING" }
  | {
      state: "ERROR";
      errorType: "INVALID_BUNDLE" | "UNKNOWN";
      errorMessage?: string;
    }
  | ({
      state: "LOADED_INCOMPATIBLE";
      onConfirmUpload: () => void;
      onCancelUpload: () => void;
    } & {
      migrationDetails: { key: string; detail: string }[];
    })
  | {
      state: "LOADED_COMPATIBLE"; // indicates compatible but want to warn users that we will wipe their test table
      onConfirmUpload: () => void;
      onCancelUpload: () => void;
    };

export const toolEditorDropZoneHeight = 150;

export const ToolEditorFormCodeUploadCodeBody = () => {
  const uploadStatus = useToolEditorContext((s) => s.codeUploadStatus);
  const uploadFile = useToolEditorContext((s) => s.uploadFile);

  const codeUploadProgress = useToolEditorContext((s) => s.codeUploadProgress);
  const toolVersionId = useToolEditorContext((s) => s.toolVersionId);
  const hasTestTable = useToolEditorContext((s) => s.testTable !== null);

  const [selectedFile, setSelectedFile] = useState<File>();
  const [fileState, setFileState] = useState<ToolEditorBundleFileState>();

  useEffect(() => {
    if (selectedFile !== undefined) {
      setFileState({ state: "LOADING" });

      /**
       * Backend will do the key comparison but handle this here for demo
       */

      validateBundle(selectedFile, toolVersionId)
        .then((res) => {
          const onConfirmUpload = () => {
            setFileState(undefined);
            setSelectedFile(undefined);
            void uploadFile(res.bundleCodeSlice, res.metadata, res.checksum);
          };
          const onCancelUpload = () => {
            setFileState(undefined);
            setSelectedFile(undefined);
          };
          /**
           * Backend has determined bundle is valid (i.e. supported version and correct metadata format)
           */
          if (res.is_valid) {
            /**
             * No spec migration needed so we can just automatically trigger the upload.
             */
            if (res.is_compatible) {
              if (hasTestTable) {
                /**
                 * Bundle is compatible but user has a test table.
                 * We will warn them about wiping the test table before uploading.
                 */
                return setFileState({
                  state: "LOADED_COMPATIBLE",
                  onConfirmUpload,
                  onCancelUpload,
                });
              }
              /**
               * Bundle is compatible and user has no test table.
               * Auto trigger upload.
               */
              onConfirmUpload();
            } else {
              /**
               * Bundle is not compatible, meaning spec will need to be migrated.
               * Give users a chance to review and confirm before proceeding with upload.
               */
              setFileState({
                state: "LOADED_INCOMPATIBLE",
                migrationDetails: res.migration_details,
                onConfirmUpload,
                onCancelUpload,
              });
            }
          } else {
            /**
             * Bundle is invalid.
             * Display warning to user.
             */
            setSelectedFile(undefined);
            setFileState({
              state: "ERROR",
              errorType: "INVALID_BUNDLE",
              errorMessage: res.error,
            });
          }
        })
        .catch((err) => {
          /**
           * Frontend has determined bundle is invalid when parsing header
           */
          if (err instanceof InvalidBundleError) {
            setSelectedFile(undefined);
            setFileState({ state: "ERROR", errorType: "INVALID_BUNDLE" });
          } else {
            setSelectedFile(undefined);
            setFileState({ state: "ERROR", errorType: "UNKNOWN" });
          }
        });
    }
  }, [selectedFile, toolVersionId, uploadFile, hasTestTable]);

  return (
    <EuiPanel
      grow
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <EuiText size="s">
        <h3>Upload your Code Bundle</h3>
      </EuiText>
      <EuiSpacer />

      {fileState !== undefined && (
        <ToolEditorFormCodeFileState fileState={fileState} />
      )}
      {uploadStatus === "UPLOADING" && (
        <EuiFlexGroup
          direction="column"
          alignItems="center"
          gutterSize="s"
          justifyContent="center"
          css={css`
            height: ${toolEditorDropZoneHeight}px;
          `}
        >
          <EuiProgress value={codeUploadProgress ?? 0} max={100} />
          <EuiText>Uploading</EuiText> {codeUploadProgress ?? 0}%
        </EuiFlexGroup>
      )}

      {fileState?.state === undefined && uploadStatus === "FAILED" && (
        <CallOutError message="Your upload failed." />
      )}

      {/* Don't show drop zone while uploading */}
      {uploadStatus !== "UPLOADING" &&
        (fileState?.state === "ERROR" || fileState?.state === undefined) && (
          /* Show drop zone in the following states
            - No file loaded
            - File failed to load
        */
          <EuiFlexItem grow>
            <DropZoneBasic
              isClickable
              accept=".cbundle"
              multiple={false}
              css={css`
                display: flex;
                max-height: ${toolEditorDropZoneHeight}px;
                min-height: ${toolEditorDropZoneHeight}px;
                flex: 1;
                border: 2px dashed lightgray;
                border-radius: 10px;
                justify-content: center;
                align-items: center;
              `}
              onSelectFiles={(files) => {
                const file = files[0];
                setSelectedFile(file);
              }}
              textOverride="Drop your .cbundle file here or click to browse files"
            />
          </EuiFlexItem>
        )}
    </EuiPanel>
  );
};
